import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import { useScreenBuilder } from '../../ScreenBuilderContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Target, 
  AlertTriangle,
  CheckCircle,
  Download,
} from 'lucide-react';

const COLLAPSE_VISIBLE_COUNT = 10;
const MIN_COLLAPSE_BUTTON_COUNT = 10;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface BacktestResult {
  summary: {
    total_return: number;
    annualized_return: number;
    volatility: number;
    sharpe_ratio: number;
    sortino_ratio: number;
    max_drawdown: number;
    win_rate: number;
    total_trades: number;
    avg_trade_return: number;
    profit_factor: number;
    calmar_ratio: number | null;
    avg_holding_days: number;
    benchmark_value?: number;
  };
  params?: {
    benchmark_symbol?: string;
    benchmark_total_return?: number;
  };
  timeseries: Array<{
    date: string;
    portfolio_value: number;
    benchmark_value?: number;
    drawdown: number;
  }>;
  trades: Array<{
    date: string;
    picked?: string[];
    turnover?: number;
    cost?: number;
    target_weights?: Record<string, number>;
    symbol?: string;
    action?: 'buy' | 'sell';
    quantity?: number;
    price?: number;
    fees?: number;
    slippage?: number;
    reason?: string;
    pnl?: number;
  }>;
  trades_roundtrip?: Array<{
    ticker: string;
    entry_date: string;
    entry_price: number;
    exit_date: string;
    exit_price: number;
    return: number;
    holding_days: number;
  }>;
  top_contributors?: Array<{
    ticker: string;
    contribution_value: number;
    contribution_pct: number;
  }>;
  top_detractors?: Array<{
    ticker: string;
    contribution_value: number;
    contribution_pct: number;
  }>;
  position_jumps?: Array<{
    date: string;
    ticker: string;
    total_return: number;
    price_return: number;
    dividend: number;
    dividend_return: number;
    price_prev: number;
    price_curr: number;
  }>;
  data_handling?: {
    exclude_large_moves?: boolean;
    show_large_moves?: boolean;
  };
  explain?: Array<{
    date: string;
    factor_table: Array<{
      ticker: string;
      pe: number;
      price: number;
      source: string;
      eps_ttm?: number;
    }>;
    selected: string[];
  }>;
  warnings?: string[];
  defaults_applied?: string[];
}

type PeriodReturn = {
  period: string;
  ret: number;
};

type DerivedMetrics = {
  max_drawdown_mag: number | null;
  worst_month: PeriodReturn | null;
  worst_quarter: PeriodReturn | null;
  q01: number | null;
  q05: number | null;
  time_under_water_fraction: number | null;
  max_time_under_water_days: number | null;
};

type InformationRatioMetrics = {
  info_ratio: number | null;
  periods_per_year: number | null;
  sample_size: number;
};

type RebalanceReturnRow = {
  period_start: string;
  period_end: string;
  start_value: number;
  end_value: number;
  period_return: number;
};

type NormalizedPoint = {
  date: string;
  ms: number;
  value: number;
};

const normalizeTimeseries = (timeseries: BacktestResult['timeseries'] | undefined): NormalizedPoint[] => {
  if (!Array.isArray(timeseries)) return [];
  const pts: NormalizedPoint[] = [];

  for (const pt of timeseries) {
    const date = pt?.date;
    if (typeof date !== 'string' || !date) continue;
    const ms = Date.parse(`${date}T00:00:00Z`);
    if (!Number.isFinite(ms)) continue;
    const value = Number((pt as any)?.portfolio_value);
    if (!Number.isFinite(value)) continue;
    pts.push({ date, ms, value });
  }

  pts.sort((a, b) => a.ms - b.ms);
  return pts;
};

const quantile = (values: number[], p: number): number | null => {
  const xs = values
    .filter((v) => Number.isFinite(v))
    .slice()
    .sort((a, b) => a - b);
  if (!xs.length) return null;

  const idx = (xs.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return xs[lo];

  const w = idx - lo;
  return xs[lo] + (xs[hi] - xs[lo]) * w;
};

const monthKey = (isoDate: string): string | null => (isoDate.length >= 7 ? isoDate.slice(0, 7) : null);

const quarterKey = (isoDate: string): string | null => {
  if (isoDate.length < 7) return null;
  const year = isoDate.slice(0, 4);
  const month = Number(isoDate.slice(5, 7));
  if (!Number.isFinite(month) || month < 1 || month > 12) return null;
  const q = Math.floor((month - 1) / 3) + 1;
  return `${year} Q${q}`;
};

const aggregateReturns = (
  datedReturns: Array<{ date: string; ret: number }>,
  periodFn: (isoDate: string) => string | null
): PeriodReturn[] => {
  const acc = new Map<string, number>();
  for (const r of datedReturns) {
    if (!Number.isFinite(r.ret)) continue;
    const key = periodFn(r.date);
    if (!key) continue;
    const prev = acc.get(key) ?? 1;
    acc.set(key, prev * (1 + r.ret));
  }
  return Array.from(acc.entries()).map(([period, prod]) => ({ period, ret: prod - 1 }));
};

const worstPeriod = (periodReturns: PeriodReturn[]): PeriodReturn | null => {
  let worst: PeriodReturn | null = null;
  for (const pr of periodReturns) {
    if (!Number.isFinite(pr.ret)) continue;
    if (!worst || pr.ret < worst.ret) worst = pr;
  }
  return worst;
};

const computeDerivedMetrics = (timeseries: BacktestResult['timeseries'] | undefined): DerivedMetrics => {
  const pts = normalizeTimeseries(timeseries);
  if (pts.length < 2) {
    return {
      max_drawdown_mag: null,
      worst_month: null,
      worst_quarter: null,
      q01: null,
      q05: null,
      time_under_water_fraction: null,
      max_time_under_water_days: null,
    };
  }

  const datedReturns: Array<{ date: string; ret: number }> = [];
  for (let i = 1; i < pts.length; i += 1) {
    const prev = pts[i - 1].value;
    const curr = pts[i].value;
    if (!Number.isFinite(prev) || prev <= 0) continue;
    if (!Number.isFinite(curr)) continue;
    datedReturns.push({ date: pts[i].date, ret: curr / prev - 1 });
  }

  const retVals = datedReturns.map((r) => r.ret);
  const q01 = quantile(retVals, 0.01);
  const q05 = quantile(retVals, 0.05);

  const worst_month = worstPeriod(aggregateReturns(datedReturns, monthKey));
  const worst_quarter = worstPeriod(aggregateReturns(datedReturns, quarterKey));

  let peakValue = pts[0].value;
  let peakMs = pts[0].ms;
  let maxDrawdown = 0;
  let underwaterCount = 0;
  let inDrawdown = false;
  let maxTuwMs = 0;

  for (let i = 1; i < pts.length; i += 1) {
    const { value, ms } = pts[i];
    if (value >= peakValue) {
      if (inDrawdown) {
        maxTuwMs = Math.max(maxTuwMs, ms - peakMs);
        inDrawdown = false;
      }
      peakValue = value;
      peakMs = ms;
      continue;
    }

    const dd = peakValue > 0 ? (peakValue - value) / peakValue : 0;
    if (dd > maxDrawdown) maxDrawdown = dd;
    underwaterCount += 1;
    inDrawdown = true;
  }

  if (inDrawdown) {
    maxTuwMs = Math.max(maxTuwMs, pts[pts.length - 1].ms - peakMs);
  }

  return {
    max_drawdown_mag: maxDrawdown,
    worst_month,
    worst_quarter,
    q01,
    q05,
    time_under_water_fraction: underwaterCount / pts.length,
    max_time_under_water_days: Math.round(maxTuwMs / MS_PER_DAY),
  };
};

const computeInformationRatio = (
  timeseries: BacktestResult['timeseries'] | undefined
): InformationRatioMetrics => {
  if (!Array.isArray(timeseries)) {
    return { info_ratio: null, periods_per_year: null, sample_size: 0 };
  }

  const pts: Array<{ ms: number; value: number; benchmark: number }> = [];
  for (const pt of timeseries) {
    const date = pt?.date;
    if (typeof date !== 'string' || !date) continue;
    const ms = Date.parse(`${date}T00:00:00Z`);
    if (!Number.isFinite(ms)) continue;
    const value = Number((pt as any)?.portfolio_value);
    const benchmark = Number((pt as any)?.benchmark_value);
    if (!Number.isFinite(value) || !Number.isFinite(benchmark)) continue;
    pts.push({ ms, value, benchmark });
  }

  if (pts.length < 2) {
    return { info_ratio: null, periods_per_year: null, sample_size: 0 };
  }

  pts.sort((a, b) => a.ms - b.ms);

  const excessReturns: number[] = [];
  for (let i = 1; i < pts.length; i += 1) {
    const prev = pts[i - 1];
    const curr = pts[i];
    if (prev.value <= 0 || prev.benchmark <= 0) continue;
    if (curr.value <= 0 || curr.benchmark <= 0) continue;
    const portRet = curr.value / prev.value - 1;
    const benchRet = curr.benchmark / prev.benchmark - 1;
    if (!Number.isFinite(portRet) || !Number.isFinite(benchRet)) continue;
    excessReturns.push(portRet - benchRet);
  }

  if (excessReturns.length < 2) {
    return { info_ratio: null, periods_per_year: null, sample_size: excessReturns.length };
  }

  const mean = excessReturns.reduce((acc, v) => acc + v, 0) / excessReturns.length;
  const variance = excessReturns.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (excessReturns.length - 1);
  const stdDev = Math.sqrt(variance);
  if (!Number.isFinite(stdDev) || stdDev === 0) {
    return { info_ratio: null, periods_per_year: null, sample_size: excessReturns.length };
  }

  const spanDays = (pts[pts.length - 1].ms - pts[0].ms) / MS_PER_DAY;
  const periodsPerYear = spanDays > 0
    ? excessReturns.length / (spanDays / 365.25)
    : 252;

  const infoRatio = mean / stdDev * Math.sqrt(periodsPerYear);
  return {
    info_ratio: Number.isFinite(infoRatio) ? infoRatio : null,
    periods_per_year: Number.isFinite(periodsPerYear) ? periodsPerYear : null,
    sample_size: excessReturns.length,
  };
};

const buildRebalanceReturnSeries = (
  timeseries: BacktestResult['timeseries'] | undefined,
  tradesSource: BacktestResult['trades'] | undefined
): RebalanceReturnRow[] => {
  const equity = normalizeTimeseries(timeseries);
  if (equity.length < 2 || !Array.isArray(tradesSource) || tradesSource.length === 0) {
    return [];
  }

  const rebalanceDates = Array.from(
    new Map(
      tradesSource
        .map((trade) => trade?.date)
        .filter((date): date is string => typeof date === 'string' && date.length > 0)
        .map((date) => {
          const ms = Date.parse(`${date}T00:00:00Z`);
          return Number.isFinite(ms) ? [ms, date] : null;
        })
        .filter((entry): entry is [number, string] => Array.isArray(entry))
    ).entries()
  )
    .map(([ms, date]) => ({ ms, date }))
    .sort((a, b) => a.ms - b.ms);

  if (rebalanceDates.length === 0) {
    return [];
  }

  const findFirstIndex = (targetMs: number) => {
    let lo = 0;
    let hi = equity.length - 1;
    let ans = -1;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (equity[mid].ms >= targetMs) {
        ans = mid;
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    return ans;
  };

  const findLastIndexBefore = (targetMs: number) => {
    let lo = 0;
    let hi = equity.length - 1;
    let ans = -1;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (equity[mid].ms < targetMs) {
        ans = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return ans;
  };

  const rows: RebalanceReturnRow[] = [];
  for (let i = 0; i < rebalanceDates.length; i += 1) {
    const startIdx = findFirstIndex(rebalanceDates[i].ms);
    if (startIdx < 0) continue;

    let endIdx = equity.length - 1;
    if (i + 1 < rebalanceDates.length) {
      const nextMs = rebalanceDates[i + 1].ms;
      const candidate = findLastIndexBefore(nextMs);
      endIdx = candidate >= 0 ? candidate : startIdx;
    }

    if (endIdx < startIdx) {
      endIdx = startIdx;
    }

    const startPt = equity[startIdx];
    const endPt = equity[endIdx];
    if (!Number.isFinite(startPt.value) || startPt.value <= 0) continue;
    if (!Number.isFinite(endPt.value)) continue;

    rows.push({
      period_start: startPt.date,
      period_end: endPt.date,
      start_value: startPt.value,
      end_value: endPt.value,
      period_return: endPt.value / startPt.value - 1,
    });
  }

  return rows;
};

interface EnhancedResultsPanelProps {
  result?: BacktestResult;
  isRunning: boolean;
}

const EnhancedResultsPanel: React.FC<EnhancedResultsPanelProps> = ({ result, isRunning }) => {
  const [selectedTrades, setSelectedTrades] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const { expertModeEnabled } = useScreenBuilder();
  const tradesSource = useMemo(() => {
    const res = result as any;
    return res?.rebalances_table || result?.trades || [];
  }, [result]);
  const derived = useMemo(() => computeDerivedMetrics(result?.timeseries), [result?.timeseries]);
  const informationRatio = useMemo(
    () => computeInformationRatio(result?.timeseries),
    [result?.timeseries]
  );
  const rebalanceReturnSeries = useMemo(
    () => buildRebalanceReturnSeries(result?.timeseries, tradesSource),
    [result?.timeseries, tradesSource]
  );

  const toggleTradeSelection = (index: number) => {
    const newSelected = new Set(selectedTrades);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedTrades(newSelected);
  };

  const toggleCellExpansion = (key: string) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatCash = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const renderExpandableItems = <T,>(
    items: T[],
    cellKey: string,
    renderItem: (item: T, idx: number) => React.ReactNode,
    containerClassName: string
  ) => {
    // Collapse long lists to the first 10 entries; skip the toggle entirely for short lists.
    const shouldShowToggle = items.length > MIN_COLLAPSE_BUTTON_COUNT;
    const isExpanded = expandedCells.has(cellKey);
    const visibleItems = shouldShowToggle && !isExpanded
      ? items.slice(0, COLLAPSE_VISIBLE_COUNT)
      : items;

    return (
      <div className={containerClassName}>
        {visibleItems.map((item, idx) => renderItem(item, idx))}
        {shouldShowToggle && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => toggleCellExpansion(cellKey)}
            title={isExpanded ? 'Show less' : 'Show all'}
          >
            ...
            <span className="sr-only">
              {isExpanded ? 'Show fewer items' : `Show all items (${items.length})`}
            </span>
          </Button>
        )}
      </div>
    );
  };

  const handleDownloadReturnSeries = () => {
    if (!rebalanceReturnSeries.length) return;
    const header = ['period_start', 'period_end', 'start_value', 'end_value', 'period_return'];
    const lines = [
      header.join(','),
      ...rebalanceReturnSeries.map((row) => [
        row.period_start,
        row.period_end,
        row.start_value.toFixed(2),
        row.end_value.toFixed(2),
        row.period_return.toFixed(6),
      ].join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `return-series-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  if (isRunning) {
    return (
      <div className="results-panel">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Running backtest...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="results-panel">
        <Card>
          <CardHeader>
            <CardTitle>Backtest Results</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 text-center py-8">
              No backtest results yet. Run a strategy to see results here.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary, timeseries, warnings, defaults_applied } = result;
  const topContributors = result.top_contributors || [];
  const topDetractors = result.top_detractors || [];
  const positionJumps = result.position_jumps || [];
  const showLargeMoves = Boolean(result.data_handling?.show_large_moves);

  // Get benchmark info from result if available
  const benchmarkSymbol = result.params?.benchmark_symbol;
  const benchmarkTotalReturn = result.params?.benchmark_total_return;

  const maxDrawdownMag = (typeof summary.max_drawdown === 'number' && Number.isFinite(summary.max_drawdown))
    ? summary.max_drawdown
    : derived.max_drawdown_mag;
  const maxDrawdownLabel = (typeof maxDrawdownMag === 'number' && Number.isFinite(maxDrawdownMag))
    ? `-${formatPercentage(maxDrawdownMag)}`
    : 'N/A';
  const worstMonthLabel = derived.worst_month
    ? `${formatPercentage(derived.worst_month.ret)} (${derived.worst_month.period})`
    : 'N/A';
  const worstQuarterLabel = derived.worst_quarter
    ? `${formatPercentage(derived.worst_quarter.ret)} (${derived.worst_quarter.period})`
    : 'N/A';
  const leftTailLabel = (typeof derived.q01 === 'number' && typeof derived.q05 === 'number')
    ? `1%: ${formatPercentage(derived.q01)} | 5%: ${formatPercentage(derived.q05)}`
    : 'N/A';
  const timeUnderWaterLabel = (
    typeof derived.time_under_water_fraction === 'number'
    && typeof derived.max_time_under_water_days === 'number'
  )
    ? `${derived.max_time_under_water_days}d (${formatPercentage(derived.time_under_water_fraction)})`
    : 'N/A';
  const infoRatioLabel = (typeof informationRatio.info_ratio === 'number' && Number.isFinite(informationRatio.info_ratio))
    ? informationRatio.info_ratio.toFixed(2)
    : 'N/A';

  return (
    <div className="results-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Backtest Results</CardTitle>
            <div className="flex items-center gap-2">
              {expertModeEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadReturnSeries}
                  disabled={!rebalanceReturnSeries.length}
                  title={rebalanceReturnSeries.length ? 'Download rebalancing return series' : 'No rebalance return series available'}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Returns CSV
                </Button>
              )}
            </div>
          </div>
          
          {defaults_applied && defaults_applied.length > 0 && (
            <div className="mt-2">
              <Badge variant="secondary" className="mb-2">
                <CheckCircle className="mr-1 h-3 w-3" />
                Defaults Applied
              </Badge>
              <div className="text-sm text-gray-600">
                {defaults_applied.join(', ')}
              </div>
            </div>
          )}
          
          {warnings && warnings.length > 0 && (
            <div className="mt-2">
              <Badge variant="destructive" className="mb-2">
                <AlertTriangle className="mr-1 h-3 w-3" />
                Warnings
              </Badge>
              <div className="text-sm text-red-600">
                {warnings.join(', ')}
              </div>
            </div>
          )}

          {benchmarkSymbol && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm font-medium text-blue-900 mb-2">
                Benchmark: <span className="font-semibold">{benchmarkSymbol}</span>
              </div>
              {benchmarkTotalReturn !== null && benchmarkTotalReturn !== undefined && (
                <div className="text-sm text-blue-800">
                  Benchmark Return: <span className="font-semibold">{formatPercentage(benchmarkTotalReturn)}</span>
                </div>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="summary">Summary</TabsTrigger>
              <TabsTrigger value="charts">Charts</TabsTrigger>
              <TabsTrigger value="trades">Rebalances</TabsTrigger>
              <TabsTrigger value="roundtrip">Trades</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="metric-card">
                  <div className="metric-header">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <span className="metric-label">Total Return</span>
                  </div>
                  <div className="metric-value">
                    {formatPercentage(summary.total_return)}
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-header">
                    <Target className="h-4 w-4 text-blue-600" />
                    <span className="metric-label">Annualized Return</span>
                  </div>
                  <div className="metric-value">
                    {formatPercentage(summary.annualized_return)}
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-header">
                    <TrendingDown className="h-4 w-4 text-orange-600" />
                    <span className="metric-label">Volatility</span>
                  </div>
                  <div className="metric-value">
                    {formatPercentage(summary.volatility)}
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-header">
                    <DollarSign className="h-4 w-4 text-purple-600" />
                    <span className="metric-label">Sharpe Ratio</span>
                  </div>
                  <div className="metric-value">
                    {summary.sharpe_ratio.toFixed(2)}
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-header">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="metric-label">Max Drawdown</span>
                  </div>
                  <div className="metric-value text-red-600">
                    -{formatPercentage(summary.max_drawdown)}
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-header">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="metric-label">Win Rate</span>
                  </div>
                  <div className="metric-value">
                    {formatPercentage(summary.win_rate)}
                  </div>
                </div>
              </div>
              
              <div className="additional-metrics">
                <h4 className="text-sm font-semibold mb-2">Additional Metrics</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total Trades:</span>
                    <span>{summary.total_trades}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg Trade Return:</span>
                    <span>{formatPercentage(summary.avg_trade_return)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Profit Factor:</span>
                    <span>{summary.profit_factor.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Max Drawdown:</span>
                    <span>{maxDrawdownLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sortino Ratio:</span>
                    <span>{summary.sortino_ratio.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Information Ratio:</span>
                    <span>{infoRatioLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Worst Month:</span>
                    <span>{worstMonthLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Worst Quarter:</span>
                    <span>{worstQuarterLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Left Tail (1%/5%):</span>
                    <span>{leftTailLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Time Under Water:</span>
                    <span>{timeUnderWaterLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg Holding Days:</span>
                    <span>{summary.avg_holding_days.toFixed(0)}</span>
                  </div>
                  {summary.calmar_ratio !== null && (
                    <div className="flex justify-between">
                      <span>Calmar Ratio:</span>
                      <span>{summary.calmar_ratio.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {(topContributors.length > 0 || topDetractors.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-3 text-green-700">Top Return Contributors</h4>
                    {topContributors.length > 0 ? (
                      <div className="space-y-2">
                        {topContributors.map((row, idx) => (
                          <div key={`${row.ticker}-${idx}`} className="flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-900">{row.ticker}</span>
                            <span className="text-green-700">
                              {formatCurrency(row.contribution_value)} ({formatPercentage(row.contribution_pct)})
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">No positive contributors.</div>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-3 text-red-700">Top Loss Contributors</h4>
                    {topDetractors.length > 0 ? (
                      <div className="space-y-2">
                        {topDetractors.map((row, idx) => (
                          <div key={`${row.ticker}-${idx}`} className="flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-900">{row.ticker}</span>
                            <span className="text-red-700">
                              {formatCurrency(row.contribution_value)} ({formatPercentage(row.contribution_pct)})
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">No negative contributors.</div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="charts" className="space-y-6">
              <div className="chart-container">
                <h4 className="text-sm font-semibold mb-2">Equity Curve</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={timeseries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="portfolio_value"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="Portfolio"
                    />
                    {timeseries && timeseries.some(pt => pt.benchmark_value !== undefined) && (
                      <Line
                        type="monotone"
                        dataKey="benchmark_value"
                        stroke="#10b981"
                        strokeWidth={2}
                        name={benchmarkSymbol ? `Benchmark (${benchmarkSymbol})` : "Benchmark"}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container">
                <h4 className="text-sm font-semibold mb-2">Drawdown</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={timeseries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatPercentage(Number(value))} />
                    <Area 
                      type="monotone" 
                      dataKey="drawdown" 
                      stroke="#ef4444" 
                      fill="#fecaca"
                      name="Drawdown"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="trades" className="space-y-4">
              <div className="trades-header">
                <h4 className="text-sm font-semibold">Rebalancing History</h4>
                <div className="text-xs text-gray-600">
                  {tradesSource.length} rebalances • {selectedTrades.size} selected
                </div>
              </div>
              
              <div className="trades-table-container">
                <table className="trades-table">
                  <thead>
                    <tr>
                      <th className="w-8"></th>
                      <th>Date</th>
                      <th>Picked Stocks</th>
                      <th>Turnover</th>
                      <th>Trading Cost</th>
                      <th>Target Weights</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradesSource.map((trade, index) => {
                      const pickedTickers = trade.picked || [];
                      const targetWeights: Array<[string, number]> = Object.entries(trade.target_weights || {})
                        .filter(([_, weight]) => (weight as number) > 0)
                        .map(([ticker, weight]) => [ticker, weight as number]);

                      return (
                        <tr key={index} className={selectedTrades.has(index) ? 'selected' : ''}>
                          <td>
                            <Checkbox
                              checked={selectedTrades.has(index)}
                              onCheckedChange={() => toggleTradeSelection(index)}
                            />
                          </td>
                          <td>{trade.date}</td>
                          <td>
                            {renderExpandableItems(
                              pickedTickers,
                              `picked-${index}`,
                              (ticker, i) => (
                                <Badge key={`${ticker}-${i}`} variant="outline" className="text-xs">
                                  {ticker}
                                </Badge>
                              ),
                              "flex flex-wrap gap-1"
                            )}
                          </td>
                          <td>{trade.turnover ? (trade.turnover * 100).toFixed(2) : '0.00'}%</td>
                          <td>{trade.cost ? formatCurrency(trade.cost) : '$0'}</td>
                          <td>
                            {renderExpandableItems(
                              targetWeights,
                              `weights-${index}`,
                              ([ticker, weight]) => (
                                <div key={ticker}>
                                  {ticker}: {(weight * 100).toFixed(1)}%
                                </div>
                              ),
                              "flex flex-col gap-1 text-xs"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {tradesSource.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No rebalances recorded for this backtest.
                </div>
              )}
            </TabsContent>

            <TabsContent value="roundtrip" className="space-y-4">
              <div className="trades-header">
                <h4 className="text-sm font-semibold">Round-Trip Trades</h4>
                <div className="text-xs text-gray-600">
                  {result.trades_roundtrip?.length || 0} trades
                </div>
              </div>
              
              <div className="trades-table-container">
                <table className="trades-table">
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th>Entry Date</th>
                      <th>Entry Price</th>
                      <th>Exit Date</th>
                      <th>Exit Price</th>
                      <th>Return</th>
                      <th>Holding Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.trades_roundtrip || []).map((trade, index) => (
                      <tr key={index} className={trade.return >= 0 ? 'text-green-600' : 'text-red-600'}>
                        <td className="font-medium">{trade.ticker}</td>
                        <td>{trade.entry_date}</td>
                        <td>{formatCurrency(trade.entry_price)}</td>
                        <td>{trade.exit_date}</td>
                        <td>{formatCurrency(trade.exit_price)}</td>
                        <td className="font-medium">
                          {formatPercentage(trade.return)}
                        </td>
                        <td>{trade.holding_days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {(!result.trades_roundtrip || result.trades_roundtrip.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  No round-trip trades recorded for this backtest.
                </div>
              )}
            </TabsContent>

          </Tabs>
        </CardContent>
      </Card>

      {showLargeMoves && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Large Daily Moves (≥ 100%)</CardTitle>
            <div className="text-sm text-gray-600">
              Positions where the combined price move and dividend payout exceeded 100% in a single day.
            </div>
          </CardHeader>
          <CardContent>
            {positionJumps.length > 0 ? (
              <div className="trades-table-container">
                <table className="trades-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Symbol</th>
                      <th>Daily Change</th>
                      <th>Price Move</th>
                      <th>Dividend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positionJumps.map((jump, index) => {
                      const totalMove = Number.isFinite(jump.total_return)
                        ? formatPercentage(jump.total_return)
                        : 'N/A';
                      const priceMove = Number.isFinite(jump.price_prev) && Number.isFinite(jump.price_curr)
                        ? `${formatPrice(jump.price_prev)} → ${formatPrice(jump.price_curr)}`
                        : 'N/A';
                      const priceMovePct = Number.isFinite(jump.price_return)
                        ? formatPercentage(jump.price_return)
                        : 'N/A';
                      const hasDividend = Number.isFinite(jump.dividend) && Math.abs(jump.dividend) > 0;
                      const dividendLabel = hasDividend
                        ? formatCash(jump.dividend)
                        : '—';
                      const dividendPct = hasDividend && Number.isFinite(jump.dividend_return)
                        ? ` (${formatPercentage(jump.dividend_return)})`
                        : '';

                      return (
                        <tr key={`${jump.ticker}-${jump.date}-${index}`}>
                          <td>{jump.date}</td>
                          <td className="font-medium">{jump.ticker}</td>
                          <td className={jump.total_return >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                            {totalMove}
                          </td>
                          <td>
                            <div className="flex flex-col">
                              <span>{priceMove}</span>
                              <span className="text-xs text-gray-500">{priceMovePct}</span>
                            </div>
                          </td>
                          <td>
                            <div className="flex flex-col">
                              <span>{dividendLabel}{dividendPct}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                No positions moved more than 100% in a single day.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnhancedResultsPanel;
